import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DeviceType } from '@renderer/types/utils'
import { Webcam } from '@renderer/lib/webcam/webcam'
import { Camera } from '@renderer/lib/web-gphoto2/camera'

interface CameraState {
  webcam: Webcam | null
  camera: Camera | null
  deviceType: DeviceType | undefined
  setWebcam: (webcam: Webcam | null) => void
  setCamera: (camera: Camera | null) => void
  setDeviceType: (type: DeviceType | undefined) => void

  secondaryWebcam: Webcam | null
  secondaryCamera: Camera | null
  secondaryDeviceType: DeviceType | undefined
  setSecondaryWebcam: (webcam: Webcam | null) => void
  setSecondaryCamera: (camera: Camera | null) => void
  setSecondaryDeviceType: (type: DeviceType | undefined) => void

  /**
   * Atomically drop a disconnected device: clears BOTH the handle and its deviceType.
   *
   * Use this instead of setCamera(null)/setWebcam(null) on teardown. Clearing only the
   * handle leaves the impossible state `deviceType === 'camera' && camera === null`, in
   * which every capture dispatch (`deviceType === 'camera' && camera`) silently matches
   * nothing and returns no frame — an un-escalatable dead branch that stranded paying
   * customers in an unbounded retry loop.
   */
  markDeviceDisconnected: (which: 'primary' | 'secondary') => void

  clearInstances: () => void
}

export const useCameraStore = create<CameraState>()(
  persist(
    (set) => ({
      webcam: null,
      camera: null,
      deviceType: 'webcam',
      setWebcam: (webcam: Webcam | null): void => void set({ webcam }),
      setCamera: (camera: Camera | null): void => void set({ camera }),
      setDeviceType: (deviceType: DeviceType | undefined): void => void set({ deviceType }),

      secondaryWebcam: null,
      secondaryCamera: null,
      secondaryDeviceType: undefined,
      setSecondaryWebcam: (webcam: Webcam | null): void => void set({ secondaryWebcam: webcam }),
      setSecondaryCamera: (camera: Camera | null): void => void set({ secondaryCamera: camera }),
      setSecondaryDeviceType: (deviceType: DeviceType | undefined): void =>
        void set({ secondaryDeviceType: deviceType }),

      markDeviceDisconnected: (which: 'primary' | 'secondary'): void =>
        void set(
          which === 'primary'
            ? { webcam: null, camera: null, deviceType: undefined }
            : { secondaryWebcam: null, secondaryCamera: null, secondaryDeviceType: undefined }
        ),

      clearInstances: (): void =>
        void set({
          webcam: null,
          camera: null,
          deviceType: 'webcam',
          secondaryWebcam: null,
          secondaryCamera: null,
          secondaryDeviceType: undefined
        })
    }),
    {
      name: 'camera-store',
      // Only persist the deviceType, not the actual instances
      partialize: (state) => ({
        deviceType: state.deviceType,
        secondaryDeviceType: state.secondaryDeviceType
      })
    }
  )
)
